// Analytics Tracking Hook for B2B Analytics
import { useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Event types
export const EventType = {
  MATERIAL_VIEW: 'material_view',
  MATERIAL_DETAIL_VIEW: 'material_detail_view',
  MATERIAL_ADDED_TO_BOARD: 'material_added_to_board',
  MATERIAL_REMOVED_FROM_BOARD: 'material_removed_from_board',
  MATERIAL_COMPARED: 'material_compared',
  MATERIAL_SAVED: 'material_saved',
  MATERIAL_SHARED: 'material_shared',
  SPEC_SHEET_DOWNLOADED: 'spec_sheet_downloaded',
  VISUAL_SEARCH: 'visual_search',
  CLIENT_FEEDBACK_APPROVED: 'client_feedback_approved',
  CLIENT_FEEDBACK_REJECTED: 'client_feedback_rejected',
  CLIENT_FEEDBACK_ALTERNATIVE: 'client_feedback_alternative',
  BOARD_CREATED: 'board_created',
  BOARD_SHARED: 'board_shared',
  COMPARISON_PDF_DOWNLOADED: 'comparison_pdf_downloaded'
};

// Generate session ID if not exists
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(7)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

export const useAnalytics = () => {
  const trackEvent = useCallback(async (eventType, data = {}) => {
    try {
      const sessionId = getSessionId();
      
      await axios.post(
        `${API}/analytics/track`,
        {
          event_type: eventType,
          material_id: data.material_id || null,
          material_ids: data.material_ids || null,
          board_id: data.board_id || null,
          metadata: data.metadata || {}
        },
        {
          withCredentials: true,
          headers: {
            'X-Session-ID': sessionId
          }
        }
      );
    } catch (error) {
      // Silently fail - analytics shouldn't break the app
      console.debug('Analytics tracking failed:', error);
    }
  }, []);

  // Convenience methods
  const trackMaterialView = useCallback((materialId, metadata = {}) => {
    trackEvent(EventType.MATERIAL_VIEW, { material_id: materialId, metadata });
  }, [trackEvent]);

  const trackMaterialDetailView = useCallback((materialId, metadata = {}) => {
    trackEvent(EventType.MATERIAL_DETAIL_VIEW, { material_id: materialId, metadata });
  }, [trackEvent]);

  const trackMaterialAddedToBoard = useCallback((materialId, boardId, metadata = {}) => {
    trackEvent(EventType.MATERIAL_ADDED_TO_BOARD, { material_id: materialId, board_id: boardId, metadata });
  }, [trackEvent]);

  const trackMaterialRemovedFromBoard = useCallback((materialId, boardId, metadata = {}) => {
    trackEvent(EventType.MATERIAL_REMOVED_FROM_BOARD, { material_id: materialId, board_id: boardId, metadata });
  }, [trackEvent]);

  const trackMaterialCompared = useCallback((materialIds, metadata = {}) => {
    trackEvent(EventType.MATERIAL_COMPARED, { material_ids: materialIds, metadata });
  }, [trackEvent]);

  const trackMaterialSaved = useCallback((materialId, metadata = {}) => {
    trackEvent(EventType.MATERIAL_SAVED, { material_id: materialId, metadata });
  }, [trackEvent]);

  const trackMaterialShared = useCallback((materialId, metadata = {}) => {
    trackEvent(EventType.MATERIAL_SHARED, { material_id: materialId, metadata });
  }, [trackEvent]);

  const trackSpecSheetDownloaded = useCallback((materialId, metadata = {}) => {
    trackEvent(EventType.SPEC_SHEET_DOWNLOADED, { material_id: materialId, metadata });
  }, [trackEvent]);

  const trackVisualSearch = useCallback((metadata = {}) => {
    trackEvent(EventType.VISUAL_SEARCH, { metadata });
  }, [trackEvent]);

  const trackClientFeedback = useCallback((materialId, feedbackType, metadata = {}) => {
    const eventMap = {
      'approved': EventType.CLIENT_FEEDBACK_APPROVED,
      'rejected': EventType.CLIENT_FEEDBACK_REJECTED,
      'request_alternative': EventType.CLIENT_FEEDBACK_ALTERNATIVE
    };
    const eventType = eventMap[feedbackType] || EventType.CLIENT_FEEDBACK_APPROVED;
    trackEvent(eventType, { material_id: materialId, metadata });
  }, [trackEvent]);

  const trackBoardCreated = useCallback((boardId, metadata = {}) => {
    trackEvent(EventType.BOARD_CREATED, { board_id: boardId, metadata });
  }, [trackEvent]);

  const trackBoardShared = useCallback((boardId, metadata = {}) => {
    trackEvent(EventType.BOARD_SHARED, { board_id: boardId, metadata });
  }, [trackEvent]);

  const trackComparisonPdfDownloaded = useCallback((materialIds, metadata = {}) => {
    trackEvent(EventType.COMPARISON_PDF_DOWNLOADED, { material_ids: materialIds, metadata });
  }, [trackEvent]);

  return {
    trackEvent,
    trackMaterialView,
    trackMaterialDetailView,
    trackMaterialAddedToBoard,
    trackMaterialRemovedFromBoard,
    trackMaterialCompared,
    trackMaterialSaved,
    trackMaterialShared,
    trackSpecSheetDownloaded,
    trackVisualSearch,
    trackClientFeedback,
    trackBoardCreated,
    trackBoardShared,
    trackComparisonPdfDownloaded
  };
};

export default useAnalytics;
